import { Link } from "react-router-dom";
import type { CrewSiteRow } from "../../api/crew";
import { formatDayMonth } from "../../api/dates";
import { directionsUrl, stopDone, stopMeta, stopNotDone, type Stop } from "./crewDay";

/** The dark card at the top of the day: where to go now, a way to get
 * there, and the way in. "Start inspection" opens the stop, where the prep
 * pack and the checklist are, as the design has it -- the checklist itself
 * is one tap further, after "what to take". */
export function NextStopCard({
  stop,
  position,
  total,
}: {
  stop: Stop;
  position: { lat: number; lon: number } | null;
  total: number;
}) {
  return (
    <section className="next-stop" aria-label="Next stop">
      <span className="next-stop-label">Next stop · {stop.arrive}</span>
      <div>
        <h2 className="next-stop-name">{stop.site.name}</h2>
        <p className="next-stop-meta">
          {stopMeta(stop.site)} · stop {stop.seq} of {total}
        </p>
      </div>
      <div className="next-stop-actions">
        {position ? (
          <a className="crew-btn crew-btn-light" href={directionsUrl(position.lat, position.lon)} target="_blank" rel="noreferrer">
            Navigate
          </a>
        ) : (
          <span className="crew-btn crew-btn-light" aria-disabled="true" title="This site has no position yet">
            No position
          </span>
        )}
        <Link className="crew-btn crew-btn-go" to={`/crew/sites/${stop.site.id}`}>
          Start inspection
        </Link>
      </div>
    </section>
  );
}

function StopMark({ stop }: { stop: Stop }) {
  if (stopDone(stop)) {
    return (
      <span className="stop-mark done" aria-label="Done">
        ✓
      </span>
    );
  }
  if (stopNotDone(stop)) {
    return (
      <span className="stop-mark not-done" aria-label="Not done">
        !
      </span>
    );
  }
  return <span className="stop-mark">{stop.seq}</span>;
}

/** One planned stop: its number or tick, the site, what the work is, and
 * when the plan has the crew arriving. The whole card is the link. */
export function StopCard({ stop, current }: { stop: Stop; current: boolean }) {
  const classes = ["stop-card", current ? "current" : "", stopDone(stop) ? "done" : ""].filter((c) => c !== "").join(" ");
  return (
    <Link className={classes} to={`/crew/sites/${stop.site.id}`}>
      <StopMark stop={stop} />
      <span className="stop-body">
        <span className="stop-name">{stop.site.name}</span>
        <span className="stop-meta">{stopMeta(stop.site)}</span>
      </span>
      <span className="stop-time">{stop.arrive}</span>
    </Link>
  );
}

/** A site with no day planned around it: "Everything" lists these. */
export function SiteCard({ site }: { site: CrewSiteRow }) {
  return (
    <Link className="stop-card" to={`/crew/sites/${site.id}`}>
      <span className="stop-mark quiet" aria-hidden="true">
        •
      </span>
      <span className="stop-body">
        <span className="stop-name">{site.name}</span>
        <span className="stop-meta">{stopMeta(site)}</span>
      </span>
      <span className="stop-time">{site.planned_date ? formatDayMonth(site.planned_date) : ""}</span>
    </Link>
  );
}
